import React, { useState, useEffect, useCallback } from 'react';
import PinpadModal from './PinpadModal';
import Button from './Button';
import Numpad from './Numpad';
import { PinpadSize } from '../../shared/types.js';
import { useTimeClock } from '../hooks/useTimeClock';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: (pin: string) => Promise<void>;
    currentPin: string;
}

const PINModal: React.FC<Props> = ({ isOpen, onClose, onSave, currentPin }) => {
    const { pinpadSize } = useTimeClock();
    const [step, setStep] = useState<'enter' | 'confirm'>('enter');
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    useEffect(() => {
        if (isOpen) {
            setStep('enter');
            setPin('');
            setConfirmPin('');
            setError('');
            setIsSaving(false);
        }
    }, [isOpen]);
    const handleNext = useCallback(() => {
        setError('');
        if (pin.length < 4) {
            setError('New PIN must be at least 4 digits.');
            return;
        }
        if (pin === currentPin) {
            setError('New PIN cannot be the same as the old PIN.');
            return;
        }
        setStep('confirm');
    }, [pin, currentPin]);
    const handleConfirm = useCallback(async () => {
        setError('');
        if (pin !== confirmPin) {
            setError('PINs do not match. Please try again.');
            setStep('enter');
            setPin('');
            setConfirmPin('');
            return;
        }
        setIsSaving(true);
        try {
            await onSave(pin);
        } catch (e: any) {
            setError(e.message || 'An unknown error occurred.');
        } finally {
            setIsSaving(false);
        }
    }, [pin, confirmPin, onSave]);
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !isSaving) {
                event.preventDefault();
                if (step === 'enter') {
                    handleNext();
                } else {
                    handleConfirm();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, step, isSaving, handleNext, handleConfirm]);
    const handleKeyPress = useCallback((key: string) => {
        if (isSaving) return;
        setError('');
        if (step === 'enter') {
            if (pin.length < 8) setPin(prev => prev + key);
        } else {
            if (confirmPin.length < 8) setConfirmPin(prev => prev + key);
        }
    }, [isSaving, step, pin.length, confirmPin.length]);
    const handleDelete = useCallback(() => {
        if (isSaving) return;
        if (step === 'enter') {
            setPin(prev => prev.slice(0, -1));
        } else {
            setConfirmPin(prev => prev.slice(0, -1));
        }
    }, [isSaving, step]);
    const handleClear = useCallback(() => {
        if (isSaving) return;
        if (step === 'enter') {
            setPin('');
        } else {
            setConfirmPin('');
        }
    }, [isSaving, step]);
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isSaving) return;
            if (event.key >= '0' && event.key <= '9') {
                event.preventDefault();
                handleKeyPress(event.key);
            } else if (event.key === 'Backspace') {
                event.preventDefault();
                handleDelete();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isSaving, handleKeyPress, handleDelete, onClose]);
    const pinpadContainerWidthClass = {
        [PinpadSize.HIDDEN]: 'w-[365px]',
        [PinpadSize.SMALL]: 'w-[365px]',
        [PinpadSize.MEDIUM]: 'w-[465px]',
        [PinpadSize.LARGE]: 'w-[665px]',
    }[pinpadSize];
    return (
        <PinpadModal isOpen={isOpen} onClose={onClose} title="Modify PIN">
            <div className={`flex flex-col items-center ${pinpadContainerWidthClass}`}>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {step === 'enter' ? 'Enter New PIN' : 'Confirm New PIN'}
                </h3>
                <input
                    type="password"
                    readOnly
                    value={(step === 'enter' ? pin : confirmPin).replace(/./g, '•')}
                    className="w-full text-center text-5xl p-4 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white mb-6"
                    aria-label="PIN input"
                />
                {error && <p className="text-red-500 text-lg mb-4 text-center">{error}</p>}
                {pinpadSize !== PinpadSize.HIDDEN &&
                    <Numpad onKeyPress={handleKeyPress} onClear={handleClear} onDelete={handleDelete} size={pinpadSize} />
                }
                <div className="flex justify-between gap-4 pt-6 mt-4 border-t border-gray-200 dark:border-gray-700 w-full">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>Cancel</Button>
                    {step === 'enter' ? (
                        <Button type="button" onClick={handleNext} disabled={isSaving}>Next</Button>
                    ) : (
                        <div className="flex gap-4">
                            <Button type="button" variant="secondary" onClick={() => { setStep('enter'); setConfirmPin(''); setError(''); }} disabled={isSaving}>Back</Button>
                            <Button type="button" onClick={handleConfirm} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save PIN'}</Button>
                        </div>
                    )}
                </div>
            </div>
        </PinpadModal>
    );
};

export default PINModal;